/**
 * User Schema Definition
 * Defines the structure and validation for user documents in Firestore
 */

/**
 * User document schema
 * @typedef {Object} UserSchema
 * @property {string} uid - Firebase Auth UID
 * @property {string} email - User's email address
 * @property {string} [displayName] - Display name (optional)
 * @property {string} [photoURL] - Profile picture URL (optional)
 * @property {string} firstName - First name
 * @property {string} lastName - Last name
 * @property {Date} [dateOfBirth] - Date of birth (optional)
 * @property {string} [gender] - Gender (optional)
 * @property {string} [accountType] - Account type: 'family' (default) or 'system_admin'
 * @property {string} familyCode - Family code linking to family (null for system_admin accounts)
 * @property {string} role - Role in family (owner, admin, member) OR system role (root_admin, system_admin, developer, etc.)
 * @property {boolean} isMainContact - Whether user is main contact for family
 * @property {Array<Object>} [relationships] - Array of relationships with other family members
 * @property {Array<string>} [familyGroups] - Groups the user belongs to
 * @property {Array<Object>} [vaultAccess] - Vault access permissions
 * @property {Date} createdAt - When account was created
 * @property {Date} updatedAt - When profile was last updated
 * @property {Date} [lastLoginAt] - Last login timestamp
 * @property {boolean} isActive - Whether account is active
 *
 * // Professional Profile Fields
 * @property {Object} [professionalProfile] - Professional profile information
 * @property {string} [professionalProfile.profession] - Primary profession/occupation
 * @property {string} [professionalProfile.jobTitle] - Current job title
 * @property {string} [professionalProfile.company] - Current company/employer
 * @property {string} [professionalProfile.industry] - Industry category
 * @property {Array<string>} [professionalProfile.skills] - Skills and expertise
 * @property {Array<Object>} [professionalProfile.services] - Services offered to family
 * @property {Array<Object>} [professionalProfile.certifications] - Professional certifications
 * @property {number} [professionalProfile.yearsOfExperience] - Years of professional experience
 * @property {Array<Object>} [professionalProfile.workHistory] - Work history entries
 * @property {Array<Object>} [professionalProfile.education] - Education entries
 * @property {boolean} [professionalProfile.isAvailableForServices] - Available to provide services
 * @property {string} [professionalProfile.hourlyRate] - Hourly rate for services
 * @property {string} [professionalProfile.availability] - Availability description
 * @property {string} [professionalProfile.serviceArea] - Geographic service area
 *
 * // Business Information
 * @property {Object} [businessInfo] - Business/company information
 * @property {string} [businessInfo.businessName] - Business name
 * @property {string} [businessInfo.businessPhone] - Business phone number
 * @property {string} [businessInfo.businessEmail] - Business email
 * @property {string} [businessInfo.website] - Business website URL
 * @property {string} [businessInfo.linkedIn] - LinkedIn profile URL
 * @property {string} [businessInfo.twitter] - Twitter/X handle or URL
 * @property {string} [businessInfo.instagram] - Instagram handle or URL
 * @property {string} [businessInfo.facebook] - Facebook page or profile URL
 * @property {Array<Object>} [businessInfo.socialProfiles] - Other social media profiles
 * @property {string} [businessInfo.portfolioUrl] - Portfolio/work samples URL
 * @property {string} [businessInfo.businessDescription] - Description of business
 * @property {boolean} [businessInfo.showInDirectory] - Whether to show in family directory
 */

/**
 * Default values for new user documents
 */
export const ACCOUNT_TYPES = {
  FAMILY: 'family',
  SYSTEM_ADMIN: 'system_admin'
};

export const defaultUserValues = {
  accountType: ACCOUNT_TYPES.FAMILY,
  role: 'member',
  isMainContact: false,
  familyGroups: [],
  vaultAccess: [],
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  professionalProfile: {
    profession: '',
    jobTitle: '',
    company: '',
    industry: '',
    skills: [],
    services: [],
    certifications: [],
    yearsOfExperience: 0,
    workHistory: [],
    education: [],
    isAvailableForServices: false,
    hourlyRate: '',
    availability: '',
    serviceArea: ''
  },
  businessInfo: {
    businessName: '',
    businessPhone: '',
    businessEmail: '',
    website: '',
    linkedIn: '',
    twitter: '',
    instagram: '',
    facebook: '',
    socialProfiles: [],
    portfolioUrl: '',
    businessDescription: '',
    showInDirectory: true
  }
};

/**
 * Industry categories for professional profiles
 */
export const industryCategories = [
  'Technology & Software',
  'Healthcare & Medical',
  'Finance & Banking',
  'Education & Training',
  'Legal Services',
  'Real Estate',
  'Construction & Trades',
  'Marketing & Advertising',
  'Creative & Design',
  'Hospitality & Food Service',
  'Retail & Sales',
  'Manufacturing',
  'Transportation & Logistics',
  'Non-Profit & Social Services',
  'Government & Public Sector',
  'Entertainment & Media',
  'Agriculture & Farming',
  'Automotive',
  'Beauty & Wellness',
  'Home Services',
  'Other'
];

/**
 * Common service categories for family directory
 */
export const serviceCategories = [
  'Accounting & Tax',
  'Auto Repair',
  'Babysitting & Childcare',
  'Catering & Cooking',
  'Cleaning Services',
  'Computer & IT Help',
  'Consulting',
  'Crafts & Handmade',
  'Electrical Work',
  'Event Planning',
  'Financial Advice',
  'Fitness & Personal Training',
  'Graphic Design',
  'Hair & Beauty',
  'Handyman Services',
  'Home Improvement',
  'Landscaping & Gardening',
  'Legal Advice',
  'Moving & Hauling',
  'Music Lessons',
  'Pet Care',
  'Photography',
  'Plumbing',
  'Real Estate',
  'Tutoring',
  'Web Development',
  'Writing & Editing',
  'Other'
];

/**
 * Required fields for user documents
 */
export const requiredUserFields = [
  'uid',
  'email',
  'firstName',
  'lastName',
  'familyCode',
  'role',
  'createdAt',
  'updatedAt',
  'isActive'
];

/**
 * Validates a user document against the schema
 * @param {Object} userData - User data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateUserData = (userData) => {
  const errors = [];

  // Check required fields (allow null for familyCode since users may not be in a family yet)
  requiredUserFields.forEach(field => {
    if (field === 'familyCode') {
      // familyCode can be null for users not yet in a family
      if (userData[field] === undefined) {
        errors.push(`Missing required field: ${field}`);
      }
    } else if (!userData[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });

  // Validate email format
  if (userData.email && !isValidEmail(userData.email)) {
    errors.push('Invalid email format');
  }

  // Validate role - allow both family roles and system roles
  const validFamilyRoles = ['owner', 'admin', 'member'];
  const validSystemRoles = ['root_admin', 'system_admin', 'support_agent', 'developer', 'ux_designer'];
  if (userData.role && ![...validFamilyRoles, ...validSystemRoles].includes(userData.role)) {
    errors.push('Invalid role. Must be a valid family or system role');
  }

  // Validate accountType
  if (userData.accountType && !Object.values(ACCOUNT_TYPES).includes(userData.accountType)) {
    errors.push('Invalid accountType. Must be one of: family, system_admin');
  }

  // Validate date fields
  if (userData.dateOfBirth && !isValidDate(userData.dateOfBirth)) {
    errors.push('Invalid date of birth format');
  }

  if (userData.createdAt && !isValidDate(userData.createdAt)) {
    errors.push('Invalid createdAt date format');
  }

  if (userData.updatedAt && !isValidDate(userData.updatedAt)) {
    errors.push('Invalid updatedAt date format');
  }

  if (userData.lastLoginAt && !isValidDate(userData.lastLoginAt)) {
    errors.push('Invalid lastLoginAt date format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Helper function to validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Helper function to validate date format
 * @param {Date|string|number} date - Date to validate
 * @returns {boolean} Whether date is valid
 */
const isValidDate = (date) => {
  if (date instanceof Date) return !isNaN(date);
  if (typeof date === 'string' || typeof date === 'number') {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate);
  }
  return false;
};

/**
 * Creates a new user document with default values
 * @param {Object} userData - Initial user data
 * @returns {Object} Complete user document with default values
 */
export const createUserDocument = (userData) => {
  const now = new Date();
  
  return {
    ...defaultUserValues,
    ...userData,
    createdAt: userData.createdAt || now,
    updatedAt: userData.updatedAt || now
  };
}; 